/**
 * The Dutch morphology this app is allowed to know.
 *
 * Two callers with two different appetites for risk share it, which is the
 * reason it is its own file rather than a private helper in either:
 *
 *   - `relevance.ts` uses it to decide *ordering*. Being wrong there costs a
 *     scroll, so it may match loosely.
 *   - `identity.ts` uses it to decide whether two shops are selling **the same
 *     product**. Being wrong there tells a household two things are one thing
 *     and points at the cheaper, so it may only fold word endings that Dutch
 *     grammar itself folds.
 *
 * Hence two functions rather than one knob. `stemPlural` and `loosenVowels`
 * are what ranking uses; `fold` is what identity uses, and it is deliberately
 * the smaller of the two.
 *
 * **What is not here, and must not be added: compounds.** A decompounder would
 * make `haver` equal `haverdrank`, which reads as a helpful synonym and is in
 * fact the rule that merges oat *drink* with oat *flour*. Dutch welds nouns
 * together and the head is the last one; splitting them is a different product
 * question and is answered — where it is answered at all — by
 * `relevance.tokenScore`, which cares which end the word sits on.
 */
/**
 * Enough morphology to match a plural against its singular:
 * *sinaasappelen* → *sinaasappel*, *eieren* → *ei*, *appels* → *appel*.
 *
 * `search_tsv` has the real snowball stemmer, but this has to run in the
 * browser and against live results that were never in the database. Left alone
 * below five letters, where stripping two characters does more harm than good.
 */
export function stemPlural(token) {
    if (token.length <= 4)
        return token;
    for (const suffix of ['eren', 'en', 'es', 's']) {
        if (token.endsWith(suffix) && token.length - suffix.length >= 3) {
            return token.slice(0, -suffix.length);
        }
    }
    return token;
}
/**
 * Dutch doubles a vowel to keep a syllable open — *tomaat* / *tomaten*,
 * *kaas* / *kazen* — so the singular and the stemmed plural differ by a letter
 * the reader does not hear. Collapsing runs of the same vowel makes the two
 * comparable. It over-matches slightly, which for a **tie-break** is the cheap
 * direction to be wrong in — and is why identity does not use it.
 */
export function loosenVowels(token) {
    return token.replace(/([aeiou])\1+/g, '$1');
}
/**
 * The identity-safe fold: the *inflections* of one word, and nothing else.
 *
 * Dutch inflects an attributive adjective with a trailing -e and doubles the
 * preceding consonant to keep the syllable closed — *vol* / *volle*, *halfvol*
 * / *halfvolle*, *vers* / *verse*, *mager* / *magere*, *biologisch* /
 * *biologische*. Two shops writing the same carton disagree about that ending
 * constantly, and no two *different* products are distinguished by it.
 *
 * That is the whole claim. `bio` still does not equal `biologisch`: they are
 * two words, not two forms of one, and merging them is a synonym table wearing
 * a stemmer's clothes (constraint 6 — no configuration).
 *
 * Applied to both sides, so it can only ever fold together words that differ
 * by an ending, never pull two words apart.
 */
export function fold(token) {
    let word = stemPlural(token);
    // "volle" → "voll" → "vol". Length guard so "the", "die", "vla" are untouched.
    if (word.length >= 5 && word.endsWith('e')) {
        word = word.slice(0, -1);
        const last = word.at(-1);
        if (!'aeiou'.includes(last) && word.at(-2) === last)
            word = word.slice(0, -1);
    }
    return word;
}
//# sourceMappingURL=dutch.js.map